import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Mail, Phone, MapPin, Clock, ArrowLeft } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';

const ContactPage = () => {
  const navigate = useNavigate();

  const contactMethods = [
    {
      icon: <Mail className="w-6 h-6 text-indigo-600" />,
      title: "General Inquiries",
      value: "support@theVinculum.de",
      href: "mailto:support@theVinculum.de"
    },
    {
      icon: <Mail className="w-6 h-6 text-green-600" />,
      title: "Sales & Demo Requests",
      value: "sales@theVinculum.de",
      href: "mailto:sales@theVinculum.de"
    },
    {
      icon: <Mail className="w-6 h-6 text-blue-600" />,
      title: "Technical Support",
      value: "helpdesk@theVinculum.de",
      href: "mailto:helpdesk@theVinculum.de"
    },
    {
      icon: <Phone className="w-6 h-6 text-purple-600" />,
      title: "Phone",
      value: "+91 97129 13584",
      href: "tel:+919712913584"
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      {/* Header */}
      <div className="bg-white border-b shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Button
            variant="ghost"
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Button>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Hero Section */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">Contact Us</h1>
          <p className="text-xl text-slate-600 max-w-2xl mx-auto">
            We're here to help you get the most out of I-Match.
          </p>
        </div>

        {/* Contact Methods Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {contactMethods.map((method, index) => (
            <Card key={index} className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <div className="flex items-center gap-3 mb-2">
                  <div className="p-2 bg-slate-100 rounded-lg">
                    {method.icon}
                  </div>
                  <CardTitle className="text-lg">{method.title}</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <a
                  href={method.href}
                  className="text-indigo-600 hover:text-indigo-700 font-medium text-lg"
                >
                  {method.value}
                </a>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Address and Support Hours */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Address */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <MapPin className="w-6 h-6 text-red-600" />
                </div>
                <CardTitle>Address</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <p className="text-slate-700 leading-relaxed">
                Vinculum,<br />
                71, Safal Vihaan,<br />
                Sarkhej Sanand Highway,<br />
                Ahmedabad
              </p>
            </CardContent>
          </Card>

          {/* Support Hours */}
          <Card>
            <CardHeader>
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 bg-slate-100 rounded-lg">
                  <Clock className="w-6 h-6 text-orange-600" />
                </div>
                <CardTitle>Support Hours</CardTitle>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p className="text-slate-700">
                  <span className="font-semibold">Monday to Friday:</span> 9:00 – 18:00
                </p>
                <p className="text-slate-600 text-sm">
                  Response time: within 24 hours
                </p>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Call to Action */}
        <div className="mt-12 text-center">
          <Card className="bg-gradient-to-r from-indigo-50 to-blue-50 border-indigo-200">
            <CardContent className="py-8">
              <h3 className="text-2xl font-bold text-slate-900 mb-4">
                Ready to get started?
              </h3>
              <p className="text-slate-600 mb-6">
                Sign up for a free trial or schedule a demo with our team.
              </p>
              <div className="flex flex-wrap justify-center gap-4">
                <Button
                  onClick={() => navigate('/register')}
                  size="lg"
                  className="bg-indigo-600 hover:bg-indigo-700"
                >
                  Start Free Trial
                </Button>
                <Button
                  variant="outline"
                  size="lg"
                  onClick={() => window.location.href = 'mailto:sales@theVinculum.de?subject=Demo Request'}
                >
                  Request Demo
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default ContactPage;
